import { syncMemberChange } from './memberSync';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { usersApi } from '../../api/users';
import { nextMemberPage, uniqueMembers } from './memberModel';
import { useRequestScope } from '../settings/shared';
export default function useMemberDiscovery(user, query, entry) {
  const client = useQueryClient(),
    request = useRequestScope(user);
  const [busy, setBusy] = useState(null),
    [mutationError, setMutationError] = useState(false);
  const lock = useRef(false),
    alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const recommended = !query;
  const queryKey = ['users', 'discover', user.id, entry.version, query];
  const list = useInfiniteQuery({
    queryKey,
    enabled: query.length !== 1,
    initialPageParam: 1,
    retry: false,
    queryFn: async ({ pageParam, signal }) => {
      const response = await usersApi.discover(query, pageParam, {
        signal,
        isSessionCurrent: entry.current,
        suppressGlobalError: true,
      });
      if (!entry.current()) throw Error('Session changed');
      if (!Array.isArray(response.data)) throw Error('Invalid members response');
      return response.data;
    },
    getNextPageParam: nextMemberPage,
  });
  const items = useMemo(
    () => uniqueMembers(list.data?.pages).filter((item) => !recommended || !item.isFollowing),
    [list.data, recommended],
  );
  const loadMore = () => {
    if (!entry.current() || !list.hasNextPage || list.isFetching || list.isError) return;
    void list.fetchNextPage({ cancelRefetch: false });
  };
  const retry = () => {
    if (list.isFetching) return;
    void (list.isFetchNextPageError
      ? list.fetchNextPage({ cancelRefetch: false })
      : list.refetch({ cancelRefetch: false }));
  };
  const toggle = async (target) => {
    if (lock.current || !entry.current() || target.id === user.id) return;
    lock.current = true;
    setBusy(target.id);
    setMutationError(false);
    const req = request();
    try {
      const response = await (target.isFollowing ? usersApi.unfollowUser : usersApi.followUser)(
        target.id,
        req.options,
      );
      if (!req.current() || !entry.current()) return;
      const updated = response.data;
      if (updated?.id !== target.id || typeof updated.isFollowing !== 'boolean')
        throw Error('Invalid follow response');
      await syncMemberChange(client, {
        viewer: user,
        target: { ...target, ...updated },
        kind: 'follow',
        current: () => req.current() && entry.current(),
      });
    } catch {
      if (req.current() && entry.current()) setMutationError(true);
    } finally {
      req.finish();
      lock.current = false;
      if (alive.current && entry.current()) setBusy(null);
    }
  };
  return { list, items, recommended, loadMore, retry, toggle, busy, mutationError };
}
